import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
	Box,
	Typography,
	Grid,
	Button,
	Stack,
	LinearProgress,
	Skeleton,
	Divider,
	Stepper,
	Step,
	StepButton,
	StepLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	Tooltip,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormControl,
	Chip,
	Paper,
} from "@mui/material";
import {
	Play,
	CheckCircle,
	Circle,
	ChevronLeft,
	Clock,
	Video,
	Music,
	FileText,
	Trophy,
	Lock,
	Award as AwardIcon,
	XCircle as CloseIcon,
} from "lucide-react";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { resolveProgressGradeId } from "../../utils/gradeProgressHelpers";
import {
	getContentDisplayTitle,
	sortContentsForUpload,
} from "../../utils/contentDisplayTitles";

// COMPLETELY REWRITTEN QuizModal with better state persistence
const QuizModal = React.memo(
	({
		quiz,
		isOpen,
		onAnswer,
		onSubmit,
		onContinue,
		userAnswer,
		quizResult,
	}) => {
		// Early return if conditions aren't met
		if (!isOpen || !quiz) {
			return null;
		}

		// Create modal content
		const modalContent = (
			<div
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "rgba(0, 0, 0, 0.8)",
					zIndex: 10000,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					pointerEvents: "all",
				}}
				onClick={(e) => {
					// Prevent closing on backdrop click
					if (e.target === e.currentTarget) {
						e.preventDefault();
						e.stopPropagation();
					}
				}}>
				<div
					style={{
						width: "90%",
						maxWidth: "500px",
						maxHeight: "90vh",
						overflow: "auto",
						pointerEvents: "all",
					}}
					onClick={(e) => {
						e.stopPropagation();
					}}>
					<Paper
						elevation={24}
						sx={{
							p: 0,
							borderRadius: 2,
							outline: "none",
							backgroundColor: "white",
						}}>
						{/* Title */}
						<Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0" }}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}>
								<Typography variant="h6">Quiz Question</Typography>
								{quizResult && (
									<Chip
										icon={
											quizResult === "correct" ? (
												<CheckCircle size={16} />
											) : (
												<CloseIcon size={16} />
											)
										}
										label={quizResult === "correct" ? "Correct!" : "Incorrect!"}
										color={quizResult === "correct" ? "success" : "error"}
									/>
								)}
							</Box>
						</Box>

						{/* Content */}
						<Box sx={{ p: 3 }}>
							<Typography variant="h6" gutterBottom>
								{quiz.question}
							</Typography>

							<FormControl component="fieldset" sx={{ mt: 2, width: "100%" }}>
								<RadioGroup
									value={userAnswer || ""}
									onChange={(e) => {
										onAnswer(e.target.value);
									}}
									disabled={quizResult !== null}>
									{["a", "b", "c", "d"].map((option) => (
										<FormControlLabel
											key={option}
											value={option}
											control={
												<Radio
													disabled={quizResult !== null}
													color={
														quizResult && option === quiz.correct_answer
															? "success"
															: quizResult &&
															  userAnswer === option &&
															  userAnswer !== quiz.correct_answer
															? "error"
															: "primary"
													}
												/>
											}
											label={
												<Box
													sx={{
														p: 1,
														border: "1px solid",
														borderColor: "divider",
														borderRadius: 1,
														bgcolor:
															quizResult && option === quiz.correct_answer
																? "success.light"
																: quizResult &&
																  userAnswer === option &&
																  userAnswer !== quiz.correct_answer
																? "error.light"
																: "background.paper",
														width: "100%",
													}}>
													<Typography>
														{option.toUpperCase()}: {quiz[`option_${option}`]}
													</Typography>
												</Box>
											}
											sx={{
												mb: 1,
												display: "flex",
												alignItems: "flex-start",
											}}
										/>
									))}
								</RadioGroup>
							</FormControl>

							{quizResult && (
								<Box sx={{ mt: 3, textAlign: "center" }}>
									<Typography
										variant="h6"
										color={
											quizResult === "correct" ? "success.main" : "error.main"
										}
										gutterBottom>
										{quizResult === "correct" ? (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}>
												<AwardIcon sx={{ mr: 1 }} />
												Great job!
											</Box>
										) : (
											<Box>
												<Typography variant="body1" sx={{ mb: 1 }}>
													The correct answer is:{" "}
													{quiz.correct_answer.toUpperCase()}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													Don't worry, you can continue with the video.
												</Typography>
											</Box>
										)}
									</Typography>
								</Box>
							)}
						</Box>

						{/* Actions */}
						<Box sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
							{!quizResult ? (
								<Button
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();

										onSubmit();
									}}
									variant="contained"
									disabled={!userAnswer}
									fullWidth>
									Submit Answer
								</Button>
							) : (
								<Button
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										onContinue();
									}}
									variant="contained"
									fullWidth>
									Continue Video
								</Button>
							)}
						</Box>
					</Paper>
				</div>
			</div>
		);

		// Get or create modal root
		let modalRoot = document.getElementById("quiz-modal-root");
		if (!modalRoot) {
			modalRoot = document.createElement("div");
			modalRoot.id = "quiz-modal-root";
			modalRoot.style.position = "fixed";
			modalRoot.style.top = "0";
			modalRoot.style.left = "0";
			modalRoot.style.width = "100%";
			modalRoot.style.height = "100%";
			modalRoot.style.pointerEvents = "none";
			modalRoot.style.zIndex = "10000";
			document.body.appendChild(modalRoot);
		}

		return createPortal(modalContent, modalRoot);
	}
);

const GameData = ({
	hideTopHeader = false,
	hideLessonDescription = false,
	/** No in-video quizzes; used by teacher lesson player. */
	disableContentQuizzes = false,
	/** Mark lesson completed when video/audio finishes (no manual confirm). */
	autoMarkCompleteOnVideoEnd = false,
	/** Explicit route ids when embedded in TeacherVideos (avoids param edge cases). */
	gradeId: gradeIdProp,
	categoryId: categoryIdProp,
}) => {
	const routeParams = useParams();
	const categoryId = categoryIdProp ?? routeParams.categoryId;
	const gradeId = gradeIdProp ?? routeParams.gradeId;
	const { user } = useAuth();
	const { currentAcademicYear } = useAcademicYear();
	const navigate = useNavigate();
	const location = useLocation();
	const videoRef = useRef(null);

	const [category, setCategory] = useState(null);
	const [contents, setContents] = useState([]);
	const [contentProgress, setContentProgress] = useState({});
	const [courseProgress, setCourseProgress] = useState(null);
	const [selectedContent, setSelectedContent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [confirmDialog, setConfirmDialog] = useState(false);
	const [contentToComplete, setContentToComplete] = useState(null);
	const [videoWatched, setVideoWatched] = useState(false);
	const [videoProgress, setVideoProgress] = useState(0);
	const [successMessage, setSuccessMessage] = useState("");

	// Quiz states - COMPLETELY REWRITTEN FOR BETTER PERSISTENCE
	const [contentQuizzes, setContentQuizzes] = useState([]);
	const [completedQuizzes, setCompletedQuizzes] = useState([]);
	const [lastValidTime, setLastValidTime] = useState(0);
	const [quizTriggered, setQuizTriggered] = useState(new Set());

	// FIXED: Better quiz modal state management
	const [currentQuiz, setCurrentQuiz] = useState(null);
	const [showQuizModal, setShowQuizModal] = useState(false);
	const [userAnswer, setUserAnswer] = useState(null);
	const [quizResult, setQuizResult] = useState(null);
	const [quizActive, setQuizActive] = useState(false);

	// Add a ref to track if we're in the middle of showing a quiz
	const quizShowingRef = useRef(false);

	const [isFullScreen, setIsFullScreen] = useState(false);
	const [teacherAssignedGrades, setTeacherAssignedGrades] = useState([]);
	const [routeGradeParent, setRouteGradeParent] = useState(null);

	useEffect(() => {
		if (!user?.id) {
			setTeacherAssignedGrades([]);
			return;
		}

		let cancelled = false;
		(async () => {
			const { data, error } = await supabase
				.from("teacher_grades")
				.select("grade_id, grades:grade_id ( id, name, parent_grade )")
				.eq("profile_id", user.id);

			if (cancelled) return;
			if (error) {
				console.error("GameData: teacher grades", error);
				setTeacherAssignedGrades([]);
				return;
			}

			setTeacherAssignedGrades(
				(data || [])
					.map((row) => ({
						id: row.grade_id,
						name: row.grades?.name || "Grade",
						parent_grade: row.grades?.parent_grade,
					}))
					.filter((g) => g.id)
			);
		})();

		return () => {
			cancelled = true;
		};
	}, [user?.id]);

	useEffect(() => {
		if (!gradeId) {
			setRouteGradeParent(null);
			return;
		}

		if (teacherAssignedGrades.some((g) => g.id === gradeId)) {
			const assigned = teacherAssignedGrades.find((g) => g.id === gradeId);
			setRouteGradeParent(assigned?.parent_grade?.trim() || null);
			return;
		}

		let cancelled = false;
		(async () => {
			const { data, error } = await supabase
				.from("grades")
				.select("parent_grade")
				.eq("id", gradeId)
				.maybeSingle();

			if (cancelled) return;
			if (error) {
				console.error("GameData: route grade parent", error);
				setRouteGradeParent(null);
				return;
			}
			setRouteGradeParent(data?.parent_grade?.trim() || null);
		})();

		return () => {
			cancelled = true;
		};
	}, [gradeId, teacherAssignedGrades]);

	/** Grade stored on progress rows — assigned section in the same parent band when needed. */
	const resolvedGradeIdForProgress = useMemo(
		() =>
			resolveProgressGradeId({
				routeGradeId: gradeId,
				assignedGrades: teacherAssignedGrades,
				categoryGradeId: category?.grade,
				courseProgressGradeId: courseProgress?.grade_id,
				routeGradeParent,
			}),
		[
			gradeId,
			teacherAssignedGrades,
			category?.grade,
			courseProgress?.grade_id,
			routeGradeParent,
		]
	);

	// Create modal root on mount
	useEffect(() => {
		// Create modal root if it doesn't exist
		if (!document.getElementById("quiz-modal-root")) {
			const modalRoot = document.createElement("div");
			modalRoot.id = "quiz-modal-root";
			modalRoot.style.position = "fixed";
			modalRoot.style.top = "0";
			modalRoot.style.left = "0";
			modalRoot.style.width = "100%";
			modalRoot.style.height = "100%";
			modalRoot.style.pointerEvents = "none";
			modalRoot.style.zIndex = "10000";
			document.body.appendChild(modalRoot);
		}

		// Cleanup function
		return () => {
			const existingModalRoot = document.getElementById("quiz-modal-root");
			if (existingModalRoot && existingModalRoot.children.length === 0) {
				try {
					document.body.removeChild(existingModalRoot);
				} catch (e) {}
			}
		};
	}, []);

	// Open a specific content when navigated from the teacher videos hub
	useEffect(() => {
		const focusContentId = location.state?.focusContentId;
		if (!focusContentId || !contents.length) return;
		const match = contents.find((c) => c.id === focusContentId);
		if (match) {
			setSelectedContent(match);
		}
	}, [location.state?.focusContentId, contents]);

	const toggleFullScreen = () => {
		if (!videoRef.current) return;

		if (!isFullScreen) {
			// Enter fullscreen
			if (videoRef.current.requestFullscreen) {
				videoRef.current.requestFullscreen();
			} else if (videoRef.current.webkitRequestFullscreen) {
				videoRef.current.webkitRequestFullscreen();
			} else if (videoRef.current.msRequestFullscreen) {
				videoRef.current.msRequestFullscreen();
			}
		} else {
			// Exit fullscreen
			if (document.exitFullscreen) {
				document.exitFullscreen();
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
			} else if (document.mozCancelFullScreen) {
				document.mozCancelFullScreen();
			} else if (document.msExitFullscreen) {
				document.msExitFullscreen();
			}
		}
	};

	// Add this effect to listen for fullscreen changes
	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullScreen(
				document.fullscreenElement ||
					document.webkitFullscreenElement ||
					document.mozFullScreenElement ||
					document.msFullscreenElement
			);
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);
		document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
		document.addEventListener("mozfullscreenchange", handleFullscreenChange);
		document.addEventListener("MSFullscreenChange", handleFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
			document.removeEventListener(
				"webkitfullscreenchange",
				handleFullscreenChange
			);
			document.removeEventListener(
				"mozfullscreenchange",
				handleFullscreenChange
			);
			document.removeEventListener(
				"MSFullscreenChange",
				handleFullscreenChange
			);
		};
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			if (!categoryId || !user) return;

			setLoading(true);
			try {
				// Fetch all data in parallel
				const [
					{ data: categoryData, error: categoryError },
					{ data: courseProgressData },
					{ data: contentsData, error: contentsError },
					{ data: progressData, error: progressError },
				] = await Promise.all([
					supabase
						.from("course_categories")
						.select("*")
						.eq("id", categoryId)
						.single(),
					supabase
						.from("teacher_course_progress")
						.select("*")
						.eq("teacher_id", user.id)
						.eq("category_id", categoryId)
						.eq("academic_year", currentAcademicYear), // Removed .single() to allow multiple results
					supabase
						.from("course_contents")
						.select("*")
						.eq("category_id", categoryId)
						.order("created_at", { ascending: true }),
					supabase
						.from("teacher_content_progress")
						.select("*")
						.eq("teacher_id", user.id)
						.eq("category_id", categoryId)
						.eq("academic_year", currentAcademicYear),
				]);

				if (categoryError) throw categoryError;
				if (contentsError) throw contentsError;
				if (progressError) throw progressError;

				// Map progress data by content ID for easier lookup
				const progressMap = progressData?.reduce(
					(acc, progress) => ({
						...acc,
						[progress.content_id]: progress,
					}),
					{}
				);

				setCategory(categoryData);

				const sortedContents = sortContentsForUpload(contentsData || []);

				setContents(sortedContents);
				setContentProgress(progressMap || {});

				// Handle multiple course progress entries — prefer the route grade
				const routeCourseProgress = resolvedGradeIdForProgress
					? courseProgressData?.find(
							(cp) => cp.grade_id === resolvedGradeIdForProgress
						)
					: null;
				setCourseProgress(routeCourseProgress || courseProgressData?.[0] || null);

				// Select the first content by default
				if (sortedContents?.length > 0) {
					setSelectedContent(sortedContents[0]);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [categoryId, user?.id, currentAcademicYear, resolvedGradeIdForProgress]);

	// Fetch quizzes when a content is selected
	useEffect(() => {
		if (disableContentQuizzes) {
			setContentQuizzes([]);
			setCompletedQuizzes([]);
			return;
		}
		if (selectedContent && user?.id) {
			const fetchQuizzes = async () => {
				try {
					// Fetch quizzes for this content
					const { data: quizData, error: quizError } = await supabase
						.from("game_content_quizzes")
						.select("*")
						.eq("content_id", selectedContent.id)
						.order("quiz_order");

					if (quizError) {
						console.error("Error fetching quizzes:", quizError);
						return;
					}

					setContentQuizzes(quizData || []);

					// Fetch completed quizzes for this user and content
					if (quizData && quizData.length > 0) {
						const { data: responses, error: respError } = await supabase
							.from("teacher_game_quiz_responses")
							.select("quiz_id")
							.eq("teacher_id", user.id)
							.eq("academic_year", currentAcademicYear)
							.in(
								"quiz_id",
								quizData.map((q) => q.id)
							);

						if (respError) {
							console.error("Error fetching quiz responses:", respError);
							return;
						}

						// Set completed quizzes based on responses
						const completedIds = responses?.map((r) => r.quiz_id) || [];
						setCompletedQuizzes(completedIds);
					} else {
						setCompletedQuizzes([]);
					}
				} catch (error) {
					console.error("Error in fetchQuizzes:", error);
				}
			};

			fetchQuizzes();
		}
	}, [
		selectedContent?.id,
		user?.id,
		currentAcademicYear,
		disableContentQuizzes,
	]);

	// FIXED: Reset states when content changes - but preserve quiz state if one is active
	useEffect(() => {
		if (selectedContent) {
			// Only reset if no quiz is currently showing
			if (!quizShowingRef.current) {
				setCurrentQuiz(null);
				setShowQuizModal(false);
				setUserAnswer(null);
				setQuizResult(null);
				setQuizActive(false);
				setQuizTriggered(new Set());
			}

			// Check if the content is already completed
			if (contentProgress[selectedContent.id]?.status === "completed") {
				setVideoWatched(true);
			} else {
				const currentProgress =
					contentProgress[selectedContent.id]?.progress_data?.progress || 0;
				setVideoWatched(currentProgress >= 95);
				setVideoProgress(currentProgress);
			}
		}
	}, [selectedContent?.id, contentProgress]);

	// Update last valid time regularly during playback
	useEffect(() => {
		const interval = setInterval(() => {
			if (
				videoRef.current &&
				videoRef.current.paused === false &&
				!quizActive
			) {
				setLastValidTime(videoRef.current.currentTime);
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [quizActive]);

	// Updated handleStartContent function
	const handleStartContent = async (contentId) => {
		try {
			

			// Create course progress if not started
			if (!courseProgress) {
				try {
					// Check if category has a direct grade assignment
					if (!category.grade) {
						

						// Try to get grades from category_grades table
						const { data: categoryGrades, error: categoryGradesError } =
							await supabase
								.from("category_grades")
								.select("grade_id")
								.eq("category_id", categoryId);

						if (categoryGradesError) {
							console.error(
								"Error fetching category grades:",
								categoryGradesError
							);
							// Don't return, continue to video playback
						} else if (categoryGrades && categoryGrades.length > 0) {
							const progressGradeId =
								resolvedGradeIdForProgress || categoryGrades[0].grade_id;

							const { data: courseData, error: courseError } = await supabase
								.from("teacher_course_progress")
								.insert([
									{
										teacher_id: user.id,
										category_id: categoryId,
										grade_id: progressGradeId,
										status: "in_progress",
										academic_year: currentAcademicYear,
									},
								])
								.select();

							if (!courseError) {
								setCourseProgress(
									courseData.find((cp) => cp.grade_id === progressGradeId) ||
										courseData[0]
								);
							}
						} else {
							console.warn(
								"Category has no grade assignments in either direct grade or category_grades table"
							);
							// Don't return, continue to video playback
						}
					} else {
						// Category has direct grade assignment - use existing logic
						

						try {
							// First, get the current grade's parent grade
							const { data: currentGradeData, error: gradeError } =
								await supabase
									.from("grades")
									.select("parent_grade")
									.eq("id", category.grade) // Using category.grade (correct field name)
									.single();

							if (!gradeError) {
								const parentGrade = currentGradeData.parent_grade;

								// Get all grades that the teacher has access to
								const { data: teacherGrades, error: teacherGradesError } =
									await supabase
										.from("teacher_grades")
										.select("grade_id")
										.eq("profile_id", user.id);

								if (!teacherGradesError) {
									// Get all grades with the same parent grade that the teacher has access to
									const { data: relatedGrades, error: relatedGradesError } =
										await supabase
											.from("grades")
											.select("id")
											.eq("parent_grade", parentGrade)
											.in(
												"id",
												teacherGrades.map((tg) => tg.grade_id)
											);

									if (!relatedGradesError) {
										// Check if the category exists for each related grade
										const categoryCheckPromises = relatedGrades.map((grade) =>
											supabase
												.from("category_grades")
												.select("category_id")
												.eq("category_id", categoryId)
												.eq("grade_id", grade.id)
												.single()
										);

										const categoryCheckResults = await Promise.allSettled(
											categoryCheckPromises
										);

										// Filter grades where the category exists
										const validGrades = relatedGrades.filter(
											(grade, index) =>
												categoryCheckResults[index].status === "fulfilled" &&
												categoryCheckResults[index].value.data
										);

										// Create course progress entries for all valid grades
										const courseProgressEntries = validGrades.map((grade) => ({
											teacher_id: user.id,
											category_id: categoryId,
											grade_id: grade.id,
											status: "in_progress",
											academic_year: currentAcademicYear,
										}));

										const { data: courseData, error: courseError } =
											await supabase
												.from("teacher_course_progress")
												.insert(courseProgressEntries)
												.select();

										if (!courseError) {
											// Set the course progress for the current grade
											const currentGradeCourseProgress = courseData.find(
												(cp) => cp.grade_id === category.grade
											);
											setCourseProgress(currentGradeCourseProgress);
											
										}
									}
								}
							}
						} catch (gradeError) {
							console.error("Error in grade-related operations:", gradeError);
							// Don't return, continue to video playback
						}
					}
				} catch (dbError) {
					console.error("Error in database operations:", dbError);
					// Don't return, continue to video playback
				}
			}

			// Create content progress if not started
			if (!contentProgress[contentId]) {
				try {
					const { data: contentProgressData, error: contentProgressError } =
						await supabase
							.from("teacher_content_progress")
							.insert([
								{
									teacher_id: user.id,
									content_id: contentId,
									category_id: categoryId,
									grade_id: resolvedGradeIdForProgress,
									progress_data: { progress: 0 },
									status: "in_progress",
									academic_year: currentAcademicYear,
								},
							])
							.select();

					if (!contentProgressError) {
						// Update local state
						setContentProgress((prev) => ({
							...prev,
							[contentId]: contentProgressData[0],
						}));
					}
				} catch (contentError) {
					console.error("Error creating content progress:", contentError);
					// Don't return, continue to video playback
				}
			}

			// ALWAYS attempt to start playing the video, regardless of database errors
			if (videoRef.current) {

				try {
					// Check if video is ready to play
					if (videoRef.current.readyState >= 2) {
						// HAVE_CURRENT_DATA
						await videoRef.current.play();
					} else {
						// Wait for video to be ready
						videoRef.current.addEventListener(
							"canplay",
							async () => {
								try {
									await videoRef.current.play();
								} catch (playError) {
									console.error(
										"Error playing video after canplay:",
										playError
									);
								}
							},
							{ once: true }
						);
					}
				} catch (playError) {
					console.error("Error playing video:", playError);
					// Video might need user interaction - this is normal browser behavior
				}
			} else {
				console.warn("Video reference not available");
			}

		} catch (error) {
			console.error("Error starting content:", error);
			// Even if there's an error, try to play the video
			if (videoRef.current) {
				try {
					await videoRef.current.play();
				} catch (playError) {
					console.error("Final attempt to play video failed:", playError);
				}
			}
		}
	};

	// Ensure progress rows exist before auto-complete on video end (teacher lesson player).
	useEffect(() => {
		if (
			!autoMarkCompleteOnVideoEnd ||
			!selectedContent?.id ||
			!user?.id ||
			contentProgress[selectedContent.id]
		) {
			return;
		}
		void handleStartContent(selectedContent.id);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run when lesson selection changes
	}, [autoMarkCompleteOnVideoEnd, selectedContent?.id, user?.id]);

	// Updated handleCompleteContent function - mark all duplicate records as completed

	const handleCompleteContent = async (contentId, silent = false) => {
		if (!contentId) return;

		if (!silent) setLoading(true);
		try {
			// First, let's check how many records exist for this combination
			const { data: existingRecords, error: checkError } = await supabase
				.from("teacher_content_progress")
				.select("*")
				.eq("teacher_id", user.id)
				.eq("content_id", contentId)
				.eq("category_id", categoryId)
				.eq("academic_year", currentAcademicYear);

			if (checkError) throw checkError;


			let updatedRecord;

			const contentGradeId = resolvedGradeIdForProgress;

			if (existingRecords && existingRecords.length > 0) {
				// Mark ALL existing records as completed (handles duplicates gracefully)
				const progressPatch = {
					status: "completed",
					progress_data: { progress: 100 },
					completed_at: new Date().toISOString(),
					grade_id: contentGradeId,
				};

				const { data, error } = await supabase
					.from("teacher_content_progress")
					.update(progressPatch)
					.eq("teacher_id", user.id)
					.eq("content_id", contentId)
					.eq("category_id", categoryId)
					.eq("academic_year", currentAcademicYear)
					.select();

				if (error) throw error;


				// Use the first updated record for local state
				updatedRecord = data?.[0];
			} else {
				// No record exists, create one
				const { data, error } = await supabase
					.from("teacher_content_progress")
					.insert([
						{
							teacher_id: user.id,
							category_id: categoryId,
							content_id: contentId,
							grade_id: contentGradeId ?? null,
							status: "completed",
							progress_data: { progress: 100 },
							completed_at: new Date().toISOString(),
							academic_year: currentAcademicYear,
						},
					])
					.select()
					.single();

				if (error) throw error;
				updatedRecord = data;
			}

			// Update local progress state
			setContentProgress((prev) => {
				const updated = {
					...prev,
					[contentId]: updatedRecord,
				};
				return updated;
			});

			// Continue with the rest of the function (course progress update logic)
			// Check if all contents are completed
			const allContents = contents.every(
				(content) =>
					contentProgress[content.id]?.status === "completed" ||
					content.id === contentId
			);
			const completedContents = contents.filter(
				(content) =>
					contentProgress[content.id]?.status === "completed" ||
					content.id === contentId
			).length;
			const completionPercentage = Math.round(
				(completedContents / contents.length) * 100
			);

			// If all contents are completed, update course progress for all related grades
			if (allContents && resolvedGradeIdForProgress) {
				const { data: currentGradeData, error: gradeError } = await supabase
					.from("grades")
					.select("parent_grade")
					.eq("id", resolvedGradeIdForProgress)
					.single();

				if (gradeError) throw gradeError;

				const parentGrade = currentGradeData.parent_grade;

				// Get all grades that the teacher has access to
				const { data: teacherGrades, error: teacherGradesError } =
					await supabase
						.from("teacher_grades")
						.select("grade_id")
						.eq("profile_id", user.id);

				if (teacherGradesError) throw teacherGradesError;

				// Get all grades with the same parent grade that the teacher has access to
				const { data: relatedGrades, error: relatedGradesError } =
					await supabase
						.from("grades")
						.select("id")
						.eq("parent_grade", parentGrade)
						.in(
							"id",
							teacherGrades.map((tg) => tg.grade_id)
						);

				if (relatedGradesError) throw relatedGradesError;

				// Check if the category exists for each related grade and get existing progress
				const existingProgressPromises = relatedGrades.map((grade) =>
					supabase
						.from("teacher_course_progress")
						.select("*")
						.eq("teacher_id", user.id)
						.eq("category_id", categoryId)
						.eq("grade_id", grade.id)
						.eq("academic_year", currentAcademicYear)
						.single()
				);

				const existingProgressResults = await Promise.allSettled(
					existingProgressPromises
				);

				// Update course progress for all grades that have existing progress
				const updatePromises = relatedGrades
					.filter(
						(grade, index) =>
							existingProgressResults[index].status === "fulfilled" &&
							existingProgressResults[index].value.data
					)
					.map((grade) =>
						supabase
							.from("teacher_course_progress")
							.update({
								status: "completed",
								completion_percentage: 100,
							})
							.eq("teacher_id", user.id)
							.eq("category_id", categoryId)
							.eq("grade_id", grade.id)
							.eq("academic_year", currentAcademicYear)
					);

				await Promise.all(updatePromises);

				
			}

			// Update course progress for current grade - FIXED to handle missing/duplicate records
			try {
				// Check if course progress exists first
				const { data: existingCourseProgress, error: courseCheckError } =
					await supabase
						.from("teacher_course_progress")
						.select("*")
						.eq("teacher_id", user.id)
						.eq("category_id", categoryId)
						.eq("grade_id", resolvedGradeIdForProgress)
						.eq("academic_year", currentAcademicYear);

				if (courseCheckError) throw courseCheckError;

				let updatedCourseProgress;

				if (existingCourseProgress && existingCourseProgress.length > 0) {
					// Update existing course progress (use first record if multiple exist)
					const { data, error: courseError } = await supabase
						.from("teacher_course_progress")
						.update({
							status: allContents ? "completed" : "in_progress",
							completion_percentage: completionPercentage,
						})
						.eq("id", existingCourseProgress[0].id)
						.select()
						.single();

					if (courseError) throw courseError;
					updatedCourseProgress = data;
				} else {
					// Create new course progress if none exists
					const { data, error: courseError } = await supabase
						.from("teacher_course_progress")
						.insert([
							{
								teacher_id: user.id,
								category_id: categoryId,
								grade_id: resolvedGradeIdForProgress,
								status: allContents ? "completed" : "in_progress",
								completion_percentage: completionPercentage,
								academic_year: currentAcademicYear,
							},
						])
						.select()
						.single();

					if (courseError) throw courseError;
					updatedCourseProgress = data;
				}

				setCourseProgress(updatedCourseProgress);
			} catch (courseError) {
				console.error("Error updating course progress:", courseError);
				// Don't throw here - content was still marked as completed successfully
			}

			// Show success message and update UI immediately
			if (!silent) {
				setSuccessMessage("Content marked as completed successfully!");
				setTimeout(() => setSuccessMessage(""), 3000);
			}

			// Force re-fetch of content progress to ensure UI is updated
			try {
				const { data: refreshedProgress, error: refreshError } = await supabase
					.from("teacher_content_progress")
					.select("*")
					.eq("teacher_id", user.id)
					.eq("category_id", categoryId)
					.eq("academic_year", currentAcademicYear);

				if (!refreshError && refreshedProgress) {
					const refreshedProgressMap = refreshedProgress.reduce(
						(acc, progress) => ({
							...acc,
							[progress.content_id]: progress,
						}),
						{}
					);
					setContentProgress(refreshedProgressMap);
				}
			} catch (refreshError) {
				console.error("Error refreshing progress:", refreshError);
			}
		} catch (error) {
			console.error("Error completing content:", error);
			if (silent) {
				setSuccessMessage(
					"Could not save lesson progress. Please replay the video or contact support."
				);
				setTimeout(() => setSuccessMessage(""), 5000);
			} else {
				setSuccessMessage(
					"Error marking content as completed. Please try again."
				);
				setTimeout(() => setSuccessMessage(""), 3000);
			}
		} finally {
			if (!silent) setLoading(false);
		}
	};

	// Helper function to exit fullscreen
	const exitFullscreen = () => {
		if (document.exitFullscreen) {
			document.exitFullscreen();
		} else if (document.webkitExitFullscreen) {
			document.webkitExitFullscreen();
		} else if (document.mozCancelFullScreen) {
			document.mozCancelFullScreen();
		} else if (document.msExitFullscreen) {
			document.msExitFullscreen();
		}
	};

	// COMPLETELY REWRITTEN: Video time update handler with rock-solid quiz triggering
	const handleVideoTimeUpdate = useCallback(() => {
		if (!videoRef.current) return;

		const video = videoRef.current;
		const currentTime = video.currentTime;
		const duration = video.duration;

		// CRITICAL: Exit early if quiz is active or showing
		if (quizActive || quizShowingRef.current) {
			return;
		}

		// Ensure duration is valid before proceeding
		if (!duration || isNaN(duration) || duration === 0) return;

		const currentProgress = Math.floor((currentTime / duration) * 100);

		// Update progress in state
		setVideoProgress(currentProgress);

		// Save progress to database periodically (every 5% or so)
		if (currentProgress % 5 === 0 && selectedContent) {
			updateContentProgress(currentProgress);
		}

		// Check if video is almost complete (95% or more)
		if (currentProgress >= 95 && !videoWatched) {
			setVideoWatched(true);
		}

		// FIXED: Quiz triggering logic with better state management
		if (
			!disableContentQuizzes &&
			contentQuizzes.length > 0 &&
			!quizActive &&
			!video.paused &&
			!quizShowingRef.current
		) {
			const quizCount = contentQuizzes.length;

			// Find the next quiz that should be shown
			const nextQuiz = contentQuizzes.find((quiz, index) => {
				// Skip if already completed or triggered
				if (completedQuizzes.includes(quiz.id) || quizTriggered.has(quiz.id)) {
					return false;
				}

				// Calculate when this quiz should appear
				const quizPosition = (duration * (index + 1)) / (quizCount + 1);
				const tolerance = 2;

				// Check if we're at the right time for this quiz
				const isAtQuizTime =
					currentTime >= quizPosition &&
					currentTime <= quizPosition + tolerance;

				return isAtQuizTime;
			});

			if (nextQuiz) {

				// Set the ref flag IMMEDIATELY to prevent re-triggering
				quizShowingRef.current = true;

				// IMMEDIATELY set active flag and pause video
				setQuizActive(true);
				video.pause();

				// Mark quiz as triggered
				setQuizTriggered((prev) => new Set([...prev, nextQuiz.id]));

				// Handle fullscreen
				const isFullScreen =
					document.fullscreenElement ||
					document.webkitFullscreenElement ||
					document.mozFullScreenElement ||
					document.msFullscreenElement;

				if (isFullScreen) {
					exitFullscreen();
				}

				// Set quiz data with a batch update
				setCurrentQuiz(nextQuiz);
				setUserAnswer(null);
				setQuizResult(null);

				// Show modal after a brief delay to ensure state is set
				setTimeout(
					() => {
						setShowQuizModal(true);
					},
					isFullScreen ? 1000 : 300
				);
			}
		}
	}, [
		quizActive,
		disableContentQuizzes,
		contentQuizzes,
		completedQuizzes,
		quizTriggered,
		videoWatched,
		selectedContent,
	]);

	// Handle seeking event to prevent fast-forwarding
	const handleSeeking = () => {
		if (videoRef.current && !quizActive) {
			// If trying to seek forward beyond a small buffer, prevent it
			if (videoRef.current.currentTime > lastValidTime + 2) {
				videoRef.current.currentTime = lastValidTime;

				// Show a notification that fast-forwarding is disabled
				setSuccessMessage("Fast-forwarding is disabled for learning videos");
				setTimeout(() => setSuccessMessage(""), 3000);
			}
		}
	};

	const handleVideoEnded = () => {
		if (quizActive && !disableContentQuizzes) return;

		setVideoWatched(true);

		const content = selectedContent;
		if (
			autoMarkCompleteOnVideoEnd &&
			content &&
			contentProgress[content.id]?.status !== "completed"
		) {
			void handleCompleteContent(content.id, true);
			return;
		}

		updateContentProgress(100);
	};

	const updateContentProgress = async (progressValue) => {
		if (!selectedContent || !contentProgress[selectedContent.id] || quizActive)
			return;

		try {
			// Update content progress in database
			await supabase
				.from("teacher_content_progress")
				.update({
					progress_data: { progress: progressValue },
				})
				.eq("teacher_id", user.id)
				.eq("content_id", selectedContent.id)
				.eq("category_id", categoryId)
				.eq("academic_year", currentAcademicYear);

			// Update local state
			setContentProgress((prev) => ({
				...prev,
				[selectedContent.id]: {
					...prev[selectedContent.id],
					progress_data: { progress: progressValue },
				},
			}));
		} catch (error) {
			console.error("Error updating content progress:", error);
		}
	};

	// FIXED: Quiz handlers with persistent state
	const handleQuizAnswer = useCallback((answer) => {
		setUserAnswer(answer);
	}, []);

	const handleQuizSubmit = useCallback(async () => {
		if (!userAnswer || !currentQuiz) return;


		const isCorrect = userAnswer === currentQuiz.correct_answer;
		setQuizResult(isCorrect ? "correct" : "incorrect");

		// Save the response to the database
		try {
			const { data, error } = await supabase
				.from("teacher_game_quiz_responses")
				.upsert(
					{
						teacher_id: user.id,
						quiz_id: currentQuiz.id,
						selected_answer: userAnswer,
						is_correct: isCorrect,
						response_time: new Date().toISOString(),
						academic_year: currentAcademicYear,
					},
					{ onConflict: ["teacher_id", "quiz_id", "academic_year"] }
				);

			if (error) {
				console.error("Error saving quiz response:", error);
			} else {
			}

			// Add to completed quizzes
			if (!completedQuizzes.includes(currentQuiz.id)) {
				setCompletedQuizzes((prev) => [...prev, currentQuiz.id]);
			}
		} catch (error) {
			console.error("Error saving quiz response:", error);
		}
	}, [userAnswer, currentQuiz, user?.id, completedQuizzes, currentAcademicYear]);

	const handleQuizContinue = useCallback(() => {

		// Reset modal states
		setShowQuizModal(false);
		setCurrentQuiz(null);
		setUserAnswer(null);
		setQuizResult(null);

		// Clear the ref flag
		quizShowingRef.current = false;

		// Delay setting quizActive to false and resuming video
		setTimeout(() => {
			setQuizActive(false);

			// Resume video after another small delay
			setTimeout(() => {
				if (videoRef.current) {
					videoRef.current
						.play()
						.then(() => {
						})
						.catch((error) => {
							console.error("Error resuming video:", error);
						});
				}
			}, 200);
		}, 200);
	}, []);

	const getContentStatus = (content) => {
		const progress = contentProgress[content.id];
		if (!progress) return "not_started";
		if (progress.status === "completed") return "completed";
		return "in_progress";
	};

	if (loading) {
		return (
			<Box sx={{ p: 2 }}>
				<Skeleton variant="text" width={200} height={40} />
				<Skeleton variant="rectangular" width="100%" height={56} sx={{ mt: 2, borderRadius: 1 }} />
				<Skeleton variant="rectangular" height={400} sx={{ mt: 2, borderRadius: 1 }} />
			</Box>
		);
	}

	const isAllContentCompleted = courseProgress?.status === "completed";
	const activeStepIndex = Math.max(
		0,
		contents.findIndex((c) => c.id === selectedContent?.id)
	);

	return (
		<Box
			sx={{
				p: 2,
				...(hideTopHeader ? { pt: { xs: 1.5, sm: 2 } } : {}),
			}}>
			{/* Header — hidden when teacher shell already shows topic + grade */}
			{!hideTopHeader && (
				<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
					<Button
						startIcon={<ChevronLeft size={20} />}
						onClick={() =>
							gradeId
								? navigate(`/teacher/topics/${gradeId}`)
								: navigate(-1)
						}
						sx={{
							color: "#6b7280",
							"&:hover": { backgroundColor: "#f3f4f6" },
						}}>
						Back
					</Button>
					<Divider orientation="vertical" flexItem />
					<Box>
						<Typography variant="h5" sx={{ fontWeight: 600, color: "#111827" }}>
							{category?.name}
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ display: { xs: "none", sm: "block" } }}>
							{category?.description}
						</Typography>
					</Box>
				</Stack>
			)}

			{/* Completion Banner */}
			{isAllContentCompleted && (
				<Box sx={{ mb: 3 }}>
					<Alert
						icon={<Trophy size={24} />}
						severity="success"
						sx={{
							backgroundColor: "#ecfdf5",
							color: "#065f46",
							"& .MuiAlert-icon": {
								color: "#059669",
							},
							borderRadius: 1,
							"& .MuiAlert-message": {
								display: "flex",
								alignItems: "center",
								gap: 1,
							},
						}}>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
								Congratulations! You've completed this course
							</Typography>
							<Typography variant="body2">
								All {contents.length} lessons have been completed successfully
							</Typography>
						</Stack>
					</Alert>
				</Box>
			)}

			{/* Main Content — horizontal wizard above lesson; no card around video */}
			<Grid container spacing={2}>
				<Grid item xs={12}>
					{contents.length > 0 && (
						<Box
							sx={{
								mb: 1,
								// StepButton uses negative margins by default; padding avoids overflow scrollbars
								pt: 1,
								px: { xs: 0.5, sm: 1 },
								pb: 0.5,
								maxWidth: "100%",
								overflow: "visible",
							}}>
							<Stepper
								nonLinear
								activeStep={activeStepIndex}
								alternativeLabel
								sx={{
									width: "100%",
									minWidth: 0,
									"& .MuiStepButton-root": {
										margin: 0,
										padding: "10px 6px",
										boxSizing: "border-box",
									},
									"& .MuiStepLabel-root": {
										flex: "1 1 0",
										minWidth: 0,
									},
									"& .MuiStepLabel-iconContainer": {
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										height: 24,
									},
									"& .MuiStepLabel-label": {
										overflow: "hidden",
										textOverflow: "ellipsis",
										display: "-webkit-box",
										WebkitLineClamp: 2,
										WebkitBoxOrient: "vertical",
									},
									/* MUI default top:12px targets default StepButton’s pulled-up layout; with margin:0 + padding, icons sit lower — align line through icon center */
									"& .MuiStepConnector-root.MuiStepConnector-alternativeLabel": {
										top: 22,
									},
									"& .MuiStepConnector-line": {
										borderTopWidth: 2,
										borderColor: "#e5e7eb",
									},
									"& .MuiStepConnector-active .MuiStepConnector-line": {
										borderColor: "#6366f1",
									},
									"& .MuiStepConnector-completed .MuiStepConnector-line": {
										borderColor: "#059669",
									},
								}}>
								{contents.map((content) => {
									const status = getContentStatus(content);
									const prog =
										status === "in_progress" &&
										contentProgress[content.id]?.progress_data?.progress;

									return (
										<Step key={content.id} completed={status === "completed"}>
											<StepButton
												onClick={() => setSelectedContent(content)}
												optional={
													prog ? (
														<Box sx={{ mt: 0.5, maxWidth: 120, mx: "auto" }}>
															<LinearProgress
																variant="determinate"
																value={
																	contentProgress[content.id].progress_data
																		.progress
																}
																sx={{
																	height: 3,
																	borderRadius: 1,
																	bgcolor: "#e5e7eb",
																	".MuiLinearProgress-bar": {
																		bgcolor: "#6366f1",
																	},
																}}
															/>
														</Box>
													) : status === "not_started" ? (
														<Typography
															variant="caption"
															sx={{ color: "#6b7280", fontSize: "0.7rem" }}>
															Pending
														</Typography>
													) : null
												}
												sx={{
													"& .MuiStepLabel-label": {
														fontSize: { xs: "0.72rem", sm: "0.8125rem" },
														fontWeight: 600,
														lineHeight: 1.25,
														mt: 0.5,
													},
												}}>
												<StepLabel
													StepIconComponent={() =>
														status === "completed" ? (
															<CheckCircle size={22} color="#059669" />
														) : status === "in_progress" ? (
															<Clock size={22} color="#6366f1" />
														) : (
															<Circle size={22} color="#9ca3af" />
														)
													}>
													{getContentDisplayTitle(content.title)}
												</StepLabel>
											</StepButton>
										</Step>
									);
								})}
							</Stepper>
						</Box>
					)}
				</Grid>

				<Grid item xs={12}>
					{selectedContent ? (
						<Box>
								{/* Title row with icon based on content type */}
								<Stack
									direction="row"
									justifyContent="space-between"
									alignItems="center"
									sx={{ mb: hideLessonDescription ? 1.5 : 2 }}>
									<Typography
										variant="h6"
										sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
										{getContentDisplayTitle(selectedContent.title)}
									</Typography>
									{selectedContent.content_type === "Video" && (
										<Video size={24} color="#6b7280" />
									)}
									{selectedContent.content_type === "Audio" && (
										<Music size={24} color="#6b7280" />
									)}
									{selectedContent.content_type === "Text" && (
										<FileText size={24} color="#6b7280" />
									)}
								</Stack>

								{/* Introduction and Description — optional (hidden in teacher embed) */}
								{!hideLessonDescription && (
									<>
										{selectedContent.introduction && (
											<Typography variant="body2" sx={{ mb: 1 }}>
												{selectedContent.introduction}
											</Typography>
										)}
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mb: 2 }}>
											{selectedContent.description}
										</Typography>
									</>
								)}

								{/* Render the actual content */}
								{selectedContent.content_type === "Video" && (
									<Box sx={{ mb: 2 }}>
										{/* Alert for fast-forwarding */}
										{successMessage && (
											<Alert
												severity="info"
												sx={{ mb: 2 }}
												action={
													<Button
														color="inherit"
														size="small"
														onClick={() => setSuccessMessage("")}>
														<CloseIcon fontSize="inherit" />
													</Button>
												}>
												{successMessage}
											</Alert>
										)}

										{selectedContent.content ? (
											<Box sx={{ position: "relative" }}>
												<video
													key={selectedContent.content}
													width="100%"
													crossOrigin="anonymous"
													ref={videoRef}
													onTimeUpdate={handleVideoTimeUpdate}
													onEnded={handleVideoEnded}
													onSeeking={handleSeeking}
													sx={{ borderRadius: 0, display: "block" }}
													onLoadedMetadata={(e) => {
														
													}}
													onCanPlay={(e) => {
														
													}}
													onError={(e) => {
														console.error("Video error:", e);
														console.error(
															"Video error details:",
															e.target.error
														);
													}}
													onLoadStart={(e) => {
														
													}}
													controls={false}>
													<source
														src={selectedContent.content}
														type="video/mp4"
													/>
													Your browser does not support the video tag.
												</video>

												{/* Custom video controls */}
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														backgroundColor: "rgba(0,0,0,0.75)",
														borderRadius: 0,
														padding: "8px 16px",
														mt: -4,
														position: "relative",
														zIndex: 1,
													}}>
													{/* Play/Pause Button */}
													<Button
														onClick={async () => {
															if (quizActive) return;

															const video = videoRef.current;
															
															// Check if content hasn't been started yet
															if (!contentProgress[selectedContent.id]) {
																
																// Start the content first
																await handleStartContent(selectedContent.id);
															}

															// Then handle play/pause
															if (video && video.paused) {
																try {
																	await video.play();
																} catch (playError) {
																	console.error(
																		"Error playing video from play button:",
																		playError
																	);
																}
															} else if (video) {
																video.pause();
															}
														}}
														sx={{
															color: "white",
															minWidth: "36px",
															padding: "4px",
														}}>
														{videoRef.current && !videoRef.current.paused ? (
															<svg
																xmlns="http://www.w3.org/2000/svg"
																width="24"
																height="24"
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round">
																<rect x="6" y="4" width="4" height="16"></rect>
																<rect x="14" y="4" width="4" height="16"></rect>
															</svg>
														) : (
															<svg
																xmlns="http://www.w3.org/2000/svg"
																width="24"
																height="24"
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round">
																<polygon points="5 3 19 12 5 21 5 3"></polygon>
															</svg>
														)}
													</Button>

													{/* Progress bar (non-interactive) */}
													<Box
														sx={{
															flex: 1,
															mx: 2,
															height: "4px",
															backgroundColor: "rgba(255,255,255,0.3)",
															borderRadius: "2px",
															overflow: "hidden",
														}}>
														<Box
															sx={{
																height: "100%",
																width: `${videoProgress}%`,
																backgroundColor: "#6366f1",
																transition: "width 0.1s",
															}}
														/>
													</Box>

													{/* Timer */}
													<Typography
														variant="caption"
														sx={{ color: "white", ml: 1 }}>
														{videoRef.current
															? `${Math.floor(
																	videoRef.current.currentTime / 60
															  )}:${String(
																	Math.floor(videoRef.current.currentTime % 60)
															  ).padStart(2, "0")} / 
               ${Math.floor((videoRef.current.duration || 0) / 60)}:${String(
																	Math.floor(
																		(videoRef.current.duration || 0) % 60
																	)
															  ).padStart(2, "0")}`
															: "0:00 / 0:00"}
													</Typography>

													{/* Volume control */}
													<Button
														onClick={() => {
															if (quizActive) return;

															const video = videoRef.current;
															video.muted = !video.muted;
														}}
														sx={{
															color: "white",
															minWidth: "36px",
															padding: "4px",
															ml: 1,
														}}>
														{videoRef.current && videoRef.current.muted ? (
															<svg
																xmlns="http://www.w3.org/2000/svg"
																width="24"
																height="24"
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round">
																<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
																<line x1="23" y1="9" x2="17" y2="15"></line>
																<line x1="17" y1="9" x2="23" y2="15"></line>
															</svg>
														) : (
															<svg
																xmlns="http://www.w3.org/2000/svg"
																width="24"
																height="24"
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round">
																<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
																<path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
															</svg>
														)}
													</Button>

													<Button
														onClick={() => {
															if (quizActive) return;
															toggleFullScreen();
														}}
														sx={{
															color: "white",
															minWidth: "36px",
															padding: "4px",
															ml: 1,
														}}>
														{isFullScreen ? (
															<svg
																xmlns="http://www.w3.org/2000/svg"
																width="24"
																height="24"
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round">
																<path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
																<path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
																<path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
																<path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
															</svg>
														) : (
															<svg
																xmlns="http://www.w3.org/2000/svg"
																width="24"
																height="24"
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round">
																<path d="M15 3h6v6"></path>
																<path d="M9 21H3v-6"></path>
																<path d="M21 3l-7 7"></path>
																<path d="M3 21l7-7"></path>
															</svg>
														)}
													</Button>
												</Box>
											</Box>
										) : (
											<Typography color="error">
												No video content available
											</Typography>
										)}
									</Box>
								)}
								{selectedContent.content_type === "Audio" && (
									<audio
										controls
										style={{ width: "100%" }}
										ref={videoRef}
										onTimeUpdate={handleVideoTimeUpdate}
										onEnded={handleVideoEnded}>
										<source src={selectedContent.content} type="audio/mpeg" />
										Your browser does not support the audio element.
									</audio>
								)}
								{selectedContent.content_type === "Text" && (
									<Typography
										variant="body1"
										sx={{ whiteSpace: "pre-wrap", mt: 2 }}>
										{selectedContent.content}
									</Typography>
								)}

								{/* Action Buttons */}
								<Stack direction="row" spacing={2} sx={{ mt: 2 }}>
									{getContentStatus(selectedContent) === "completed" ? (
										<Button
											variant="outlined"
											size="small"
											startIcon={<CheckCircle size={16} />}
											sx={{
												color: "#059669",
												borderColor: "#059669",
												"&:hover": {
													borderColor: "#047857",
													backgroundColor: "rgba(5, 150, 105, 0.04)",
												},
											}}>
											Completed
										</Button>
									) : contentProgress[selectedContent.id] ? (
										autoMarkCompleteOnVideoEnd &&
										(selectedContent.content_type === "Video" ||
											selectedContent.content_type === "Audio") ? (
											<Typography variant="body2" color="text.secondary">
												This lesson completes automatically when playback
												finishes.
											</Typography>
										) : (
											<Tooltip
												title={
													!videoWatched &&
													selectedContent.content_type === "Video" &&
													videoProgress < 95
														? "You need to watch the video completely to mark it as complete"
														: ""
												}>
												<span>
													<Button
														variant="outlined"
														size="small"
														startIcon={
															videoWatched ||
															selectedContent.content_type !== "Video" ? (
																<CheckCircle size={16} />
															) : (
																<Lock size={16} />
															)
														}
														onClick={() => {
															if (
																videoWatched ||
																selectedContent.content_type !== "Video"
															) {
																setContentToComplete(selectedContent);
																setConfirmDialog(true);
															}
														}}
														disabled={
															selectedContent.content_type === "Video" &&
															!videoWatched &&
															videoProgress < 95
														}
														sx={{
															color: "#059669",
															borderColor: "#059669",
															"&:hover": {
																borderColor: "#047857",
																backgroundColor: "rgba(5, 150, 105, 0.04)",
															},
															"&.Mui-disabled": {
																color: "#9ca3af",
																borderColor: "#d1d5db",
															},
														}}>
														Mark Complete
													</Button>
												</span>
											</Tooltip>
										)
									) : (
										<Button
											variant="contained"
											size="small"
											startIcon={<Play size={16} />}
											onClick={() => handleStartContent(selectedContent.id)}
											sx={{
												backgroundColor: "#6366f1",
												"&:hover": {
													backgroundColor: "#4f46e5",
												},
											}}>
											Start
										</Button>
									)}
								</Stack>
						</Box>
					) : (
						<Box
							sx={{
								height: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}>
							<Typography color="text.secondary">
								Select a content to view details
							</Typography>
						</Box>
					)}
				</Grid>
			</Grid>

			{/* Confirmation Dialog */}
			<Dialog
				open={confirmDialog}
				onClose={() => setConfirmDialog(false)}
				maxWidth="xs"
				fullWidth>
				<DialogTitle sx={{ fontWeight: 600 }}>
					Mark Content as Complete?
				</DialogTitle>
				<DialogContent sx={{ px: 3, py: 2 }}>
					<Typography variant="body2" color="text.secondary">
						Are you sure you want to mark "{getContentDisplayTitle(contentToComplete?.title)}" as
						complete? This action will update your course progress.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ p: 2, pt: 1 }}>
					<Button
						onClick={() => setConfirmDialog(false)}
						variant="outlined"
						size="small"
						sx={{
							color: "#6b7280",
							borderColor: "#e5e7eb",
							"&:hover": {
								borderColor: "#d1d5db",
								backgroundColor: "#f9fafb",
							},
						}}>
						Cancel
					</Button>
					<Button
						onClick={() => {
							handleCompleteContent(contentToComplete.id);
							setConfirmDialog(false);
						}}
						variant="contained"
						size="small"
						sx={{
							backgroundColor: "#059669",
							"&:hover": {
								backgroundColor: "#047857",
							},
						}}>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>

			{/* FIXED: Quiz Modal with persistent state */}
			<QuizModal
				quiz={currentQuiz}
				isOpen={!disableContentQuizzes && showQuizModal}
				onAnswer={handleQuizAnswer}
				onSubmit={handleQuizSubmit}
				onContinue={handleQuizContinue}
				userAnswer={userAnswer}
				quizResult={quizResult}
			/>
		</Box>
	);
};

export default GameData;
